import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { MsgService } from '@app/_components/msg/msg.service';
import { first } from 'rxjs/operators';

export interface ITopicMessagesViewData {
  topic?: number;
  topicId?: number;
  msgs?: any[];
  title?: string;
}

@Component({
    selector: 'app-topic-messages-view',
    templateUrl: './topic-messages-view.component.html',
    styleUrls: ['./topic-messages-view.component.css'],
    providers: [DatePipe],
    standalone: false
})
export class TopicMessagesViewComponent implements OnInit {
  msgs: any[] = [];
  loading = true;
  error: string | null = null;
  title: string = 'Topic Messages';
  selectedRecipient: any = null;

  keyValueList = {
    "msgRecipientId": "Msg Recipient Id",
    "msgId": "Msg Id",
    "email": "Email",
    "emailRecipientType": "Email Recipient Type",
    "mobileCountryCode": "Mobile Country Code",
    "createDate": "Created Date",
    "lastUpdate": "Last Updated",
    "deliveredDate": "Delivered Date",
    "openedDate": "Last Opened Date",
    "openedDates": "Opened Dates"
  };
  
  mailStatEntries: any = [
    // { key: 'messageCount', icon: 'pi-envelope', label: 'Messages', toolTip: 'Messages Count' },
    // { key: 'sentCount', icon: 'pi-send', label: 'Sent', toolTip: 'Sent Count' },
    { key: 'deliveredCount', icon: 'pi-check', label: 'Delivered', toolTip: 'deliveredDate',tooltipText: 'Delivered Date' },
    { key: 'openedCount', icon: 'pi-eye', label: 'Opened', toolTip: 'openedDates',tooltipText: 'Opened Dates' },
    { key: 'deferredCount', icon: 'pi-clock', label: 'Deferred', toolTip: 'deferredDate',tooltipText: 'Deferred Date' },
    // { key: 'clickedCount', icon: 'pi-external-link', label: 'Clicked', toolTip: 'Clicked Count' },
    { key: 'bouncedCount', icon: 'pi-ban', label: 'Bounced', toolTip: 'bounceCount',tooltipText: 'Bounced Count' },
    // { key: 'droppedCount', icon: 'pi-minus-circle', label: 'Dropped', toolTip: 'Dropped Count',tooltipText: 'Dropped Count' }
  ];

  constructor(
    private msgService: MsgService,
    private datePipe: DatePipe,
    @Optional() public dialogRef: MatDialogRef<TopicMessagesViewComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ITopicMessagesViewData
  ) {
    if (data?.title) {
      this.title = data.title;
    }
  }

  ngOnInit(): void {
    if (this.data?.msgs && Array.isArray(this.data.msgs)) {
      this.msgs = this.data.msgs;
      this.loading = false;
      return;
    }
    if (this.data?.topic != null && this.data?.topicId != null) {
      this.msgService.getTopicMessagesRecipient(this.data.topic, this.data.topicId)
        .pipe(first())
        .subscribe({
          next: (res: any) => {
            if (res?.statusCode != null && res.statusCode >= 400) {
              this.error = res?.message || 'Failed to load messages';
              this.msgs = [];
            } else {
              this.msgs = res?.data?.msgs ?? res?.msgs ?? [];
            }
            this.loading = false;
          },
          error: (err) => {
            if (typeof err === 'string') {
              this.error = err;
            } else {
              this.error = err?.error?.message || err?.message || 'Failed to load messages';
            }
            this.loading = false;
          }
        });
      return;
    }
    this.loading = false;
    this.error = 'No topic/topicId or msgs provided.';
  }

  getRecipientEmails(msg: any): string {
    const list = msg?.msgRecipientAPIList || [];
    return list.map((r: any) => r.email).filter(Boolean).join(', ') || '—';
  }

  getOpenedDatesList(msg: any): string[] {
    const dates = new Set<string>();
    const list = msg?.msgRecipientAPIList || [];
    for (const r of list) {
      if (r.openedDate) {
        const d = this.formatDate(r.openedDate);
        if (d) dates.add(d);
      }
      if (r.openedDates && typeof r.openedDates === 'string') {
        r.openedDates.split(',').forEach((s: string) => {
          const trimmed = s.trim();
          if (trimmed) {
            const d = this.formatDate(trimmed);
            if (d) dates.add(d);
          }
        });
      }
    }
    return Array.from(dates).sort();
  }

  formatDate(value: string | Date): string {
    if (!value) return '';
    const d = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(d.getTime())) return '';
    return this.datePipe.transform(d, 'MM-dd-yyyy h:mm a') || '';
  }

  formatDateShort(value: string | Date): string {
    if (!value) return '';
    const d = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(d.getTime())) return '';
    return this.datePipe.transform(d, 'MM-dd-yyyy') || '';
  }

  close(): void {
    this.dialogRef?.close();
  }

  openRecipientDetail(recipient: any, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedRecipient = recipient;
  }

  closeRecipientDetail(): void {
    this.selectedRecipient = null;
  }

  getRecipientDetailEntries(recipient: any): { key: string; value: string }[] {
    if (!recipient) return [];
    const dateKeys = ['createDate', 'lastUpdate', 'deliveredDate', 'openedDate', 'deferredDate'];
    const keys = [
      'msgRecipientId', 'msgId', 'email',
      'createDate', 'deliveredDate', 'openedDate', 'openedDates', 'deferredDate'
    ];
    return keys
      .filter(k => recipient[k] !== undefined && recipient[k] !== null && recipient[k] !== '')
      .map(k => {
        const v = recipient[k];
        let value: string;
        if (dateKeys.includes(k) && (typeof v === 'string' || v instanceof Date)) {
          value = this.formatDate(v);
        } else if (k === 'openedDates' && typeof v === 'string') {
          value = v.split(',').map((s: string) => s.trim()).filter(Boolean).map(d => this.formatDate(d)).join(', ') || v;
        } else {
          value = String(v);
        }
        return { key: k, value };
      });
  }

  getTooltip(entry: any, stats: any): string {
    const dateColumns = ['deliveredDate', 'openedDate', 'openedDates'];
    const toolTipKey = entry?.toolTip;
    if (toolTipKey == null || toolTipKey === '') return '';

    const keys = toolTipKey.split(',').map((k: string) => k.trim()).filter(Boolean);
    const isDateColumn = keys.some((k: string) => dateColumns.includes(k));

    if (isDateColumn) {
      const dates: string[] = [];
      for (const key of keys) {
        const val = stats?.[key];
        if (val == null || val === '') continue;
        if (typeof val === 'string' && val.includes(',')) {
          val.split(',').map((s: string) => s.trim()).filter(Boolean).forEach((d: string) => {
            const formatted = this.formatDate(d);
            if (formatted) dates.push(formatted);
          });
        } else {
          const formatted = this.formatDate(val);
          if (formatted) dates.push(formatted);
        }
      }
      const heading = entry?.tooltipText ? entry.tooltipText + ':\n' : '';
      return heading + dates.join('\n');
    }

    const value = stats?.[toolTipKey];
    const prefix = entry?.tooltipText ? entry.tooltipText + ': ' : '';
    return prefix + (value != null && value !== '' ? String(value) : '');
  }

  /** For date columns returns number of dates; for others returns stats[entry.key]. */
  getDisplayCount(entry: any, stats: any): number {
    const dateColumns = ['deliveredDate', 'openedDate', 'openedDates'];
    const toolTipKey = entry?.toolTip;
    if (toolTipKey == null || toolTipKey === '') return 0;

    const keys = toolTipKey.split(',').map((k: string) => k.trim()).filter(Boolean);
    const isDateColumn = keys.some((k: string) => dateColumns.includes(k));

    if (isDateColumn) {
      let count = 0;
      for (const key of keys) {
        const val = stats?.[key];
        if (val == null || val === '') continue;
        if (typeof val === 'string' && val.includes(',')) {
          count += val.split(',').map((s: string) => s.trim()).filter(Boolean).length;
        } else {
          count += 1;
        }
      }
      return count;
    }
    const v = stats?.[entry?.key];
    return v != null && v !== '' ? Number(v) : 0;
  }
}
